import { initDB, addChannel, addVideo } from '../db/indexeddb.js';
import { getValidAccessToken } from './session.js';
import { fetchSubscriptions, fetchChannelDetails, fetchChannelVideos, fetchChannelDetailsWithKey, fetchChannelVideosWithKey } from './client.js';
import { getApiKey, getDefaultChannelId } from './config.js';

export async function runScan({ vaultKey, onProgress, onEvent }) {
  const db = await initDB();
  let token = null;
  try {
    token = await getValidAccessToken(vaultKey);
  } catch (error) {
    const apiKey = await getApiKey();
    const channelId = await getDefaultChannelId();
    if (!apiKey || !channelId) {
      throw error;
    }
    onEvent?.('OAuth unavailable. Using API key for single-channel scan.');
    await scanSingleChannel({ apiKey, channelId, db, onEvent, onProgress });
    return { totalChannels: 1, mode: 'apiKey' };
  }

  onEvent?.('Fetching subscriptions...');
  const subs = await fetchSubscriptions(token);
  const items = subs.items || [];

  onProgress?.({ queuedChannels: items.length, processedChannels: 0 });

  let processed = 0;
  for (const sub of items) {
    const channelId = sub.snippet?.resourceId?.channelId;
    if (!channelId) continue;

    onEvent?.(`Fetching channel ${sub.snippet?.title || channelId}`);
    const channelData = await fetchChannelDetails(token, channelId);
    const channel = channelData.items?.[0];

    if (channel) {
      await persistChannelWithVideos({
        channel,
        db,
        fetchVideos: (uploads) => fetchChannelVideos(token, uploads)
      });
    }

    processed += 1;
    onProgress?.({ queuedChannels: items.length, processedChannels: processed });
  }

  onEvent?.('Scan completed.');
  return { totalChannels: items.length, mode: 'oauth' };
}

async function scanSingleChannel({ apiKey, channelId, db, onEvent, onProgress }) {
  onProgress?.({ queuedChannels: 1, processedChannels: 0 });
  const channelData = await fetchChannelDetailsWithKey(apiKey, channelId);
  const channel = channelData.items?.[0];
  if (!channel) {
    throw new Error('Channel not found with API key.');
  }
  await persistChannelWithVideos({
    channel,
    db,
    fetchVideos: (uploads) => fetchChannelVideosWithKey(apiKey, uploads)
  });
  onProgress?.({ queuedChannels: 1, processedChannels: 1 });
  onEvent?.('Single-channel scan completed.');
}

async function persistChannelWithVideos({ channel, db, fetchVideos }) {
  const uploads = channel.contentDetails?.relatedPlaylists?.uploads;
  const lastVideoDate = channel.snippet?.publishedAt || null;
  await addChannel(db, {
    id: channel.id,
    title: channel.snippet?.title,
    description: channel.snippet?.description,
    thumbnail: channel.snippet?.thumbnails?.medium?.url || null,
    subscribers: channel.statistics?.subscriberCount || null,
    videoCount: channel.statistics?.videoCount || null,
    viewCount: channel.statistics?.viewCount || null,
    uploadsPlaylist: uploads || null,
    lastVideoDate
  });

  if (uploads) {
    const playlistItems = await fetchVideos(uploads);
    const videos = playlistItems.items || [];
    for (const video of videos) {
      await addVideo(db, {
        id: video.contentDetails?.videoId,
        channelId: channel.id,
        title: video.snippet?.title,
        publishedAt: video.contentDetails?.videoPublishedAt || video.snippet?.publishedAt,
        thumbnail: video.snippet?.thumbnails?.medium?.url || null
      });
    }
  }
}
